import React, { createContext, useContext, useState, useCallback } from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Slide,
  Stack,
  Box,
  IconButton,
  Typography,
  LinearProgress,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  NotificationImportant as PriorityIcon,
} from '@mui/icons-material';
import { BounceAnimation } from './Animations';

export type NotificationVariant = 'success' | 'error' | 'warning' | 'info';
export type NotificationPriority = 'low' | 'medium' | 'high' | 'urgent';

interface NotificationData {
  id: string;
  title?: string;
  message: string;
  variant: NotificationVariant;
  priority?: NotificationPriority;
  duration?: number;
  persistent?: boolean;
  actions?: NotificationAction[];
  progress?: number;
  timestamp: Date;
}

interface NotificationAction {
  label: string;
  onClick: () => void;
  variant?: 'text' | 'outlined' | 'contained';
}

interface NotificationContextType {
  notifications: NotificationData[];
  addNotification: (notification: Omit<NotificationData, 'id' | 'timestamp'>) => string;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  updateNotification: (id: string, updates: Partial<NotificationData>) => void;
  
  // Convenience methods
  showSuccess: (message: string, options?: Partial<NotificationData>) => string;
  showError: (message: string, options?: Partial<NotificationData>) => string;
  showWarning: (message: string, options?: Partial<NotificationData>) => string;
  showInfo: (message: string, options?: Partial<NotificationData>) => string;
  showProgress: (message: string, progress: number, options?: Partial<NotificationData>) => string;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

/**
 * Enhanced Notification Provider
 * Manages a sophisticated notification system with priorities, actions, and animations
 */
export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);

  const addNotification = useCallback((notification: Omit<NotificationData, 'id' | 'timestamp'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newNotification: NotificationData = {
      id,
      timestamp: new Date(),
      duration: 5000,
      priority: 'medium',
      ...notification,
    };

    setNotifications(prev => {
      // Sort by priority (urgent first, then by timestamp)
      const priorities = { urgent: 4, high: 3, medium: 2, low: 1 };
      const updated = [...prev, newNotification];
      return updated.sort((a, b) => {
        const priorityDiff = priorities[b.priority || 'medium'] - priorities[a.priority || 'medium'];
        if (priorityDiff !== 0) return priorityDiff;
        return b.timestamp.getTime() - a.timestamp.getTime();
      });
    });

    // Auto-remove non-persistent notifications
    if (!notification.persistent && notification.duration !== 0) {
      setTimeout(() => {
        removeNotification(id);
      }, notification.duration || 5000);
    }

    return id;
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const updateNotification = useCallback((id: string, updates: Partial<NotificationData>) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, ...updates } : n)
    );
  }, []);

  // Convenience methods
  const showSuccess = useCallback((message: string, options?: Partial<NotificationData>) => {
    return addNotification({ message, variant: 'success', ...options });
  }, [addNotification]);

  const showError = useCallback((message: string, options?: Partial<NotificationData>) => {
    return addNotification({ message, variant: 'error', priority: 'high', ...options });
  }, [addNotification]);

  const showWarning = useCallback((message: string, options?: Partial<NotificationData>) => {
    return addNotification({ message, variant: 'warning', priority: 'medium', ...options });
  }, [addNotification]);

  const showInfo = useCallback((message: string, options?: Partial<NotificationData>) => {
    return addNotification({ message, variant: 'info', ...options });
  }, [addNotification]);

  const showProgress = useCallback((message: string, progress: number, options?: Partial<NotificationData>) => {
    return addNotification({ message, variant: 'info', progress, persistent: true, ...options });
  }, [addNotification]);

  return (
    <NotificationContext.Provider value={{
      notifications,
      addNotification,
      removeNotification,
      clearAll,
      updateNotification,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      showProgress,
    }}>
      {children}
      <NotificationContainer notifications={notifications} onRemove={removeNotification} />
    </NotificationContext.Provider>
  );
};

/**
 * Notification Container Component
 */
interface NotificationContainerProps {
  notifications: NotificationData[];
  onRemove: (id: string) => void;
}

const NotificationContainer: React.FC<NotificationContainerProps> = ({ notifications, onRemove }) => {
  const theme = useTheme();
  const maxVisible = 5; // Show max 5 notifications at once
  const visibleNotifications = notifications.slice(0, maxVisible);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 24,
        right: 24,
        zIndex: 2000,
        width: 400,
        maxWidth: 'calc(100vw - 48px)',
      }}
    >
      <Stack spacing={1}>
        {visibleNotifications.map((notification, index) => (
          <Slide
            key={notification.id}
            direction="left"
            in={true}
            timeout={300}
            style={{ transitionDelay: `${index * 100}ms` }}
          >
            <div>
              {notification.priority === 'urgent' ? (
                <BounceAnimation>
                  <NotificationItem
                    notification={notification}
                    onRemove={() => onRemove(notification.id)}
                  />
                </BounceAnimation>
              ) : (
                <NotificationItem
                  notification={notification}
                  onRemove={() => onRemove(notification.id)}
                />
              )}
            </div>
          </Slide>
        ))}
        
        {/* Show count if there are more notifications */}
        {notifications.length > maxVisible && (
          <Alert
            severity="info"
            variant="outlined"
            sx={{
              backgroundColor: alpha(theme.palette.info.main, 0.1),
              borderRadius: 2,
            }}
          >
            <Typography variant="body2">
              +{notifications.length - maxVisible} more notification{notifications.length - maxVisible !== 1 ? 's' : ''}
            </Typography>
          </Alert>
        )}
      </Stack>
    </Box>
  );
};

/**
 * Individual Notification Item
 */
interface NotificationItemProps {
  notification: NotificationData;
  onRemove: () => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onRemove }) => {
  const theme = useTheme();

  const getIcon = () => {
    switch (notification.variant) {
      case 'success': return <SuccessIcon />;
      case 'error': return <ErrorIcon />;
      case 'warning': return <WarningIcon />;
      case 'info': return <InfoIcon />;
      default: return <InfoIcon />;
    }
  };

  const getPriorityColor = () => {
    switch (notification.priority) {
      case 'urgent': return theme.palette.error.main;
      case 'high': return theme.palette.warning.main;
      case 'medium': return theme.palette.info.main;
      case 'low': return theme.palette.grey[500];
      default: return theme.palette.info.main;
    }
  };

  return (
    <Alert
      severity={notification.variant}
      variant="filled"
      onClose={onRemove}
      icon={getIcon()}
      sx={{
        borderRadius: 2,
        boxShadow: theme.shadows[4],
        border: notification.priority === 'urgent' ? `2px solid ${theme.palette.error.main}` : 'none',
        animation: notification.priority === 'urgent' ? 'pulse 2s infinite' : 'none',
        '@keyframes pulse': {
          '0%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.02)' },
          '100%': { transform: 'scale(1)' },
        },
      }}
    >
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: notification.title ? 1 : 0 }}>
          <Box sx={{ flexGrow: 1 }}>
            {notification.title && (
              <AlertTitle sx={{ fontSize: '1rem', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {notification.title}
                  {notification.priority === 'urgent' && (
                    <PriorityIcon sx={{ fontSize: '1rem', color: 'inherit' }} />
                  )}
                </Box>
              </AlertTitle>
            )}
            <Typography variant="body2" sx={{ color: 'inherit' }}>
              {notification.message}
            </Typography>
          </Box>

          {/* Priority Indicator */}
          {notification.priority && notification.priority !== 'medium' && (
            <Chip
              label={notification.priority.toUpperCase()}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 'bold',
                backgroundColor: alpha(getPriorityColor(), 0.2),
                color: getPriorityColor(),
                ml: 1,
              }}
            />
          )}
        </Box>

        {/* Progress Bar */}
        {notification.progress !== undefined && (
          <Box sx={{ mt: 1, mb: 1 }}>
            <LinearProgress
              variant="determinate"
              value={notification.progress}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.common.white, 0.3),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  backgroundColor: theme.palette.common.white,
                },
              }}
            />
            <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.9, mt: 0.5, display: 'block' }}>
              {Math.round(notification.progress)}% complete
            </Typography>
          </Box>
        )}

        {/* Actions */}
        {notification.actions && notification.actions.length > 0 && (
          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {notification.actions.map((action, index) => (
              <button
                key={index}
                onClick={() => {
                  action.onClick();
                  onRemove();
                }}
                style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  border: 'none',
                  backgroundColor: alpha(theme.palette.common.white, 0.2),
                  color: 'inherit',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                }}
              >
                {action.label}
              </button>
            ))}
          </Box>
        )}

        {/* Timestamp */}
        <Typography
          variant="caption"
          sx={{
            color: 'inherit',
            opacity: 0.7,
            display: 'block',
            mt: notification.actions ? 1 : 0.5,
            fontSize: '0.7rem',
          }}
        >
          {notification.timestamp.toLocaleTimeString()}
        </Typography>
      </Box>
    </Alert>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

/**
 * Notification Hook for Components
 */
export const useNotify = () => {
  const { showSuccess, showError, showWarning, showInfo, showProgress } = useNotifications();
  
  return {
    success: showSuccess,
    error: showError,
    warning: showWarning,
    info: showInfo,
    progress: showProgress,
  };
};